import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
}                                        from '@angular/core';
import {
	AbstractControl,
	FormGroup,
	UntypedFormArray,
	UntypedFormGroup,
	Validators
}                                        from '@angular/forms';
import { BaseComponent }                 from '@app/core/bases/base-component/base.component';
import {
	ENTITY_TYPE,
	IEntity
}                                        from '@app/core/interfaces/entity.interface';
import {
	FORM_ACTION,
	FormSubmitActionEnum,
	IFormResult,
	IFormUpdateData
}                                        from '@app/core/interfaces/form.interface';
import { SelectVersionModalService }     from '@app/core/modals/select-version-modal/select-version-modal.service';
import { AppInjector }                   from '@app/core/services/app-injector.service';
import { actionStatus }                  from '@app/shared/services/toaster.service';
import {
	RequestCompleteEntityState,
	RequestState
}                                        from '@core/interfaces/api.interface';
import { IStoreConfig }                  from '@core/interfaces/store.interface';
import { SelectVersionModalActionsEnum } from '@core/modals/select-version-modal/select-version-modal.config';
import { FormUtils }                     from '@shared/forms/helpers';
import { ObjectUtils }                   from '@utils/helpers';
import {
	Observable,
	Subject
}                                        from 'rxjs';
import {
	take,
	takeUntil
}                                        from 'rxjs/operators';


type PreservePropertiesFromChangeOverwrite = ( entity: ENTITY_TYPE, key: string, val: any, fallbackValue: any ) => void;

type BeforeApiRequest = ( context ) => void;

type BeforeApiCreateRequest = () => void;
type ApiCreateRequestOverwrite = ( ...args: any[] ) => Observable<RequestCompleteEntityState<ENTITY_TYPE>>;
type ApiCreateRequestSuccess = ( state: RequestCompleteEntityState<ENTITY_TYPE>, context ) => void;
type ApiCreateRequestFailure = ( state: RequestCompleteEntityState<ENTITY_TYPE>, context ) => void;

type ApiVersionReadRequestSuccess = ( state: RequestCompleteEntityState<ENTITY_TYPE> ) => void;
type ApiVersionReadRequestFailure = ( state: RequestCompleteEntityState<ENTITY_TYPE>, context ) => void;
type ApiVersionCheckFinished = () => void;

type ApiReadRequestOverwrite = ( ...args: any[] ) => Observable<RequestCompleteEntityState<ENTITY_TYPE>>;

type ApiUpdateRequestOverwrite = ( ...args: any[] ) => Observable<RequestCompleteEntityState<ENTITY_TYPE>>;
// type ApiUpdateRequestSuccess= ( state: RequestCompleteState<ENTITY_TYPE>, context ) => void; // maybe needed in future
// type ApiUpdateRequestFailure = ( state: RequestCompleteState<ENTITY_TYPE>, context ) => void; // maybe needed in future


// type BeforeApiUpdateRequest = () => void; // maybe needed in future

@Component( {
	selector: 'app-form-base',
	template: '<div></div>'
} )
export class FormBaseComponent extends BaseComponent implements OnInit {

	protected preservePropertiesFromChangeOverwrite: PreservePropertiesFromChangeOverwrite;
	protected apiVersionReadRequestSuccess: ApiVersionReadRequestSuccess;
	protected apiVersionCheckFinished: ApiVersionCheckFinished;

	get f() {
		return this.entityContainer.form.controls;
	}

	protected apiCreateRequestOverwrite: ApiCreateRequestOverwrite;
	protected apiReadRequestOverwrite: ApiReadRequestOverwrite;
	protected apiUpdateRequestOverwrite: ApiUpdateRequestOverwrite;

	private defaultState: string;

	protected readonly selectVersionModalService;

	protected readonly entityMetadata;

	protected entityContainer: IEntity<ENTITY_TYPE>;

	protected entityName: string;

	protected isFormModified = true;
	protected requestInProgress = false;

	protected createRequestAdditionalArguments: any[];
	protected readRequestAdditionalArguments: any[];
	protected updateRequestAdditionalArguments: any[];
	protected optOutVersionCheck: boolean;
	protected formSubmitAction: FormSubmitActionEnum;

	updateControlState: Subject<boolean> = new Subject<boolean>();

	@Input() skipShowPageLoader = false;
	@Output() requestInProgressEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() formValuesEmitter: EventEmitter<any> = new EventEmitter<any>();

	formAction: FORM_ACTION;

	formActionType = FORM_ACTION;

	formConfigData;

	hideErrors = true;

	constructor() {
		super();
		const injector = AppInjector.getInjector();
		this.selectVersionModalService = injector.get( SelectVersionModalService );
	}

	private getReadRequest( entityId: string, withFullResponse: boolean, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<ENTITY_TYPE>> {
		return this.apiReadRequestOverwrite
					 ? this.createRequestAdditionalArguments
						 ? this.apiReadRequestOverwrite( ...this.createRequestAdditionalArguments )
						 : this.apiReadRequestOverwrite( entityId, withFullResponse, storeConfig )
					 : this.entityContainer.service.read( entityId, withFullResponse, storeConfig );
	}

	private showSelectVersionModal(): void {
		this.getReadRequest( this.entityContainer.entity.id, true )
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( readState: RequestCompleteEntityState<ENTITY_TYPE> ) => {
			this.updateRequestInProgressAndPageLoader( false );

			this.toaster.dispatch( readState, this.entityName, actionStatus.READ, true );
			if ( readState.requestState !== RequestState.SUCCESS ) { return; }

			this.selectVersionModalService.selectVersion(
				readState.entity,
				this.entityContainer.entity,
				this.entityContainer.form,
				this.entityMetadata.form.baseData
			)
			.pipe(
				take( 1 ),
				takeUntil( this.ngUnsubscribe )
			)
			.subscribe( result => {

				if ( result.action === SelectVersionModalActionsEnum.CONFIRM ) {
					this.logger.debug( 'selectVersion result: ', result.data );
					this.entityContainer.formService.mapEntityToValues( result.data as ENTITY_TYPE, this.entityContainer.form );
				}

				if ( result.action === SelectVersionModalActionsEnum.ERROR ) {

				}
			} );
		} );
	}

	private handleCreateFailure(
		state: RequestCompleteEntityState<ENTITY_TYPE>,
		i18nEntity: string,
		context: any,
		apiCreateRequestFailure?: ApiCreateRequestFailure,
		showFailureToaster = true
	): void {

		let formResult = this.generateFormSubmissionResult( RequestState.FAILED, null );

		switch ( state.error.status ) {
			case 400: // invalid data supplied
				formResult = { ...formResult, ...{ action: FORM_ACTION.CREATE } };
				break;
			case 409: // entity already exists
				formResult = { ...formResult, ...{ action: FORM_ACTION.ALREADY_EXISTS } };
				break;
			default:
		}

		if ( apiCreateRequestFailure ) {
			apiCreateRequestFailure( state, context );
		}

		if ( showFailureToaster ) {
			this.toaster.dispatch( state, i18nEntity, actionStatus.ADD, true );
		}

		this.formRequestFinished( formResult );
	}

	private handleUpdateFailure( state: RequestCompleteEntityState<ENTITY_TYPE>, i18nEntity: string ): void {
		let formResult;

		switch ( state.error.status ) {
			case 412: // update version conflicts
				if ( this.optOutVersionCheck ) { return; }
				this.showSelectVersionModal();
				break;

			case 404: // entity has been deleted already
				this.entityContainer.service.storeRemoveOne( this.entityContainer.entity.id );
				this.toaster.dispatch( state, i18nEntity, actionStatus.UPDATE, true );
				formResult = this.generateFormSubmissionResult( RequestState.FAILED, null );
				formResult = { ...formResult, ...{ action: FORM_ACTION.ALREADY_DELETED } };
				this.formRequestFinished( formResult );
				break;

			default:
				formResult = this.generateFormSubmissionResult( RequestState.FAILED, null );
				formResult = { ...formResult, ...{ action: FORM_ACTION.UPDATE } };
				this.formRequestFinished( formResult );
				this.toaster.dispatch( state, i18nEntity, actionStatus.UPDATE, true );
		}
	}

	private getVersion(): void {
		this.getReadRequest( this.entityContainer.entity.id, true, { updateStore: false } )
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( readState: RequestCompleteEntityState<ENTITY_TYPE> ) => {

			this.toaster.dispatch( readState, this.entityName, actionStatus.READ, true );
			if ( readState.requestState === RequestState.FAILED ) {
				this.entityContainer.service.storeRemoveOne( this.entityContainer.entity.id );
				// combination of success requestState and update formAction, will enforce page list reload
				// list updated is needed because of already deleted entity on backend side
				let formResult = this.generateFormSubmissionResult( RequestState.FAILED, null );
				formResult = { ...formResult, ...{ action: FORM_ACTION.ALREADY_DELETED } };
				this.formRequestFinished( formResult );
			}

			this.entityContainer.entity = { ...readState.entity };

			if ( readState.requestState === RequestState.SUCCESS && this.apiVersionReadRequestSuccess ) {
				this.apiVersionReadRequestSuccess( readState );
			}

			this.finalizeUpdateInit();
			this.logger.log( 'Update: Entity with Version: ', this.entityContainer.entity );

			if ( this.apiVersionCheckFinished ) {
				this.apiVersionCheckFinished();
			}
		} );
	}

	private finalizeUpdateInit(): void {
		this.setUpdateFallbackValues( this.entityContainer.entity );
		this.entityContainer.form.markAsPristine();
		this.listenFormChanges();
	}

	/**
	 * Updates entityContainer.entity with provided data. Update & preserve mode check against given entity.
	 *
	 * @param entity against this is make a check for update, preserve mode
	 * @param updateData data to write (to entityContainer.entity), can be create or update defaults
	 * @param updateType create - write data, update, preserve - write data (fallback) only if checked entity property is empty
	 */
	private updateEntity( entity: ENTITY_TYPE, updateData: IFormUpdateData, updateType: string ): void {
		Object.values( updateData ).forEach( ( { key, val, fallbackValue } ) => {

			switch ( updateType ) {
				case 'create':
					this.entityContainer.entity[ key ] = val;
					break;
				case 'update':
				case 'preserve':

					if ( this.preservePropertiesFromChangeOverwrite ) {
						this.preservePropertiesFromChangeOverwrite( entity, key, val, fallbackValue );
					} else {

						let preserveValue = this.deepReadWithEmptyCheck( entity, key );

						if ( ObjectUtils.isEmpty( preserveValue ) ) {
							preserveValue = fallbackValue;
						}

						ObjectUtils.deepWrite( this.entityContainer.entity, key, preserveValue, true );
					}
					break;
			}
		} );
	}

	private deepReadWithEmptyCheck( entity: ENTITY_TYPE, key: string ): any {
		return ObjectUtils.isEmpty( entity[ key ] )
					 ? ObjectUtils.deepRead( entity, key )
					 : entity[ key ];
	}

	/**
	 * Set create values to container entity, resolve fallback values when needed. Maps container entity to form values.
	 *
	 * @param entity entity with source values
	 */
	private setDefaultCreateValues( entity: ENTITY_TYPE ): void {
		const formCreateData = this.entityMetadata.form.createData;
		if ( formCreateData && formCreateData.length > 0 ) {
			this.updateEntity( entity, formCreateData, 'create' );
		}
		this.mapEntityToValues();
	}

	/**
	 * Set update values to container entity, resolve fallback values when needed. Maps container entity to form values.
	 *
	 * @param entity entity with source values
	 */
	private setUpdateFallbackValues( entity: ENTITY_TYPE ): void {
		const formUpdateData = this.entityMetadata.form.updateData;
		if ( formUpdateData && formUpdateData.length > 0 ) {
			this.updateEntity( entity, formUpdateData, 'update' );
		}
		this.mapEntityToValues();
	}

	/**
	 * Preserve properties of container entity, resolve fallback values when needed. Maps container entity to form values.
	 *
	 * @param oldEntity old entity with original values
	 */
	private preservePropertiesFromChange( oldEntity: ENTITY_TYPE ): void {
		const formPreserveData = this.entityMetadata.form.preserveData;
		if ( formPreserveData && formPreserveData.length > 0 ) {
			this.updateEntity( oldEntity, formPreserveData, 'preserve' );
		}
		this.mapEntityToValues();
	}

	/**
	 * Remap container entity to form field values.
	 */
	private mapEntityToValues(): FormGroup {
		return this.entityContainer.formService.mapEntityToValues( this.entityContainer.entity, this.entityContainer.form );
	}

	private listenFormChanges(): void {
		this.defaultState = JSON.stringify( this.entityContainer.form.value );
		this.entityContainer.form.valueChanges.subscribe( formObject => {
			this.isFormModified = this.defaultState !== JSON.stringify( formObject );
		} );
	}

	protected updateRequestInProgressAndPageLoader( requestInProgress: boolean ): void {

		if ( !this.skipShowPageLoader ) {
			if ( requestInProgress ) {
				this.uiService.showPageLoaderAndCountUp();
			} else {
				this.uiService.hidePageLoaderOrCountDown();
			}
		}

		this.requestInProgress = requestInProgress;
		this.requestInProgressEmitter.emit( requestInProgress );
	}

	protected initializeForm( entity: ENTITY_TYPE, passEntityToCreateForm?: boolean ): void {
		this.entityContainer.entity = { ...entity };

		if ( passEntityToCreateForm ) {
			this.entityContainer.form = this.entityContainer.formService.createForm( this.entityContainer.entity );
		} else {
			this.entityContainer.form = this.entityContainer.formService.createForm();
		}

		this.formAction = this.getFormAction( entity );

		switch ( this.formAction ) {
			case FORM_ACTION.CREATE:
				this.setDefaultCreateValues( this.entityContainer.entity );
				break;

			case FORM_ACTION.UPDATE:
				if ( this.optOutVersionCheck ) {
					this.finalizeUpdateInit();
				} else {
					this.getVersion();
				}
				break;
		}
	}

	protected getFormAction( entity: ENTITY_TYPE ): FORM_ACTION {
		const conditionalFormAction: FORM_ACTION = entity?.id === undefined
																							 ? FORM_ACTION.CREATE
																							 : FORM_ACTION.UPDATE;

		return this.formAction ?? conditionalFormAction;
	}

	isFormValid(): boolean {
		return (
						 !this.entityContainer.form.invalid || this.hideErrors
					 ) && this.isFormModified && this.entityContainer.form.dirty;
	}

	protected submitCreateForm(
		context: any,
		apiCreateRequestSuccess?: ApiCreateRequestSuccess,
		showSuccessToaster = true,
		apiCreateRequestFailure?: ApiCreateRequestFailure,
		showFailureToaster = true
	): void {
		const entity = this.entityContainer.entity;

		const req$ = this.apiCreateRequestOverwrite
								 ? this.createRequestAdditionalArguments
									 ? this.apiCreateRequestOverwrite( ...this.createRequestAdditionalArguments )
									 : this.apiCreateRequestOverwrite( entity )
								 : this.entityContainer.service.create( entity );

		req$
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( state: RequestCompleteEntityState<ENTITY_TYPE> ) => {
			if ( state.requestState === RequestState.SUCCESS && state.entity ) {

				if ( apiCreateRequestSuccess ) {
					apiCreateRequestSuccess( state, context );
				}

				if ( showSuccessToaster ) {
					this.toaster.dispatch( state, this.entityName, actionStatus.ADD );
				}

				const formResult = this.generateFormSubmissionResult( state.requestState, state.entity );
				this.formRequestFinished( formResult );

			} else if ( state.requestState === RequestState.FAILED ) {
				this.handleCreateFailure( state, this.entityName, context, apiCreateRequestFailure, showFailureToaster );
			}
		} );
	}

	protected submitUpdateForm(): void {
		/*
		 * TODO
		 *  Further investigate why, without creating a deep copy here, properties
		 *  remain unchanged in some entities during execution of the double ternary
		 *  below, but change in others (working hypothesis: only properties of
		 *  nested objects are affected)
		 */
		const entity = ObjectUtils.deepCopyByRecursion( this.entityContainer.entity );

		const req$ = this.apiUpdateRequestOverwrite
								 ? this.updateRequestAdditionalArguments
									 ? this.apiUpdateRequestOverwrite( ...this.updateRequestAdditionalArguments )
									 : this.apiUpdateRequestOverwrite( entity )
								 : this.entityContainer.service.update( entity );

		req$
		.pipe(
			take( 1 ),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( ( state: RequestCompleteEntityState<ENTITY_TYPE> ) => {

			if ( state.requestState === RequestState.FAILED ) {
				this.handleUpdateFailure( state, this.entityName );
			}
			if ( state.requestState === RequestState.SUCCESS ) {
				this.toaster.dispatch( state, this.entityName, actionStatus.UPDATE );

				const formResult = this.generateFormSubmissionResult( RequestState.SUCCESS, state.entity );
				this.formRequestFinished( formResult );
			}
		} );
	}

	/**
	 * Whole form reset. Desired priority of data & preserve operation is:
	 *
	 * 1) preserveData + fallback  	: CREATE, UPDATE
	 * 2) createData + fallback  		: CREATE,  				updateData + fallback  		: UPDATE
	 *
	 * <b> Processing order of data to final entityContainer.entity (and mapped to form fields later): </b>
	 * - newEntity -> enentityContainer.entity
	 * - defaultCreateValues -> enentityContainer.entity | updateFallbackValues -> enentityContainer.entity
	 * - preservePropertiesFromChange -> (checking oldEntity, if empty field) -> enentityContainer.entity
	 *
	 * @param markPristineOnCreate on form action FORM_ACTION.CREATE marks form as pristine (when true which is default).
	 * Reason:
	 * SetDefaultCreateValues makes form dirty.
	 * Preserving values with defaults can make changes against reseted form. Some field types sometimes has an unexpected
	 * values after the reset, e.g. number (null -> 0) and boolean (null -> false).
	 */
	protected baseResetForm( oldEntity: ENTITY_TYPE, newEntity: ENTITY_TYPE, markPristineOnCreate: boolean = true ): void {
		if ( this.entityContainer?.entity?.etag ) {
			oldEntity = { ...oldEntity, ...{ etag: this.entityContainer.entity.etag } };
		}
		this.entityContainer.entity = { ...newEntity };

		// iterate form for optional field values for future compare
		const optionalControlsNotEmpty: AbstractControl[] = [];  // accumulator
		this.getOptionalValuesOfForm( this.entityContainer.form, optionalControlsNotEmpty );  // get opt. fields with values

		this.hideErrors = true;
		this.entityContainer.form.reset();

		switch ( this.formAction ) {
			case FORM_ACTION.CREATE:
				this.setDefaultCreateValues( this.entityContainer.entity );
				// Preserve is #1 priority, hence last-one
				if ( !ObjectUtils.isEmpty( oldEntity ) ) {  // only when some old entity exists, in create mode mostly for preserved values handling
					this.preservePropertiesFromChange( oldEntity );
				}

				if ( markPristineOnCreate ) {
					this.entityContainer.form.markAsPristine();
				}
				break;

			case FORM_ACTION.UPDATE:
				this.setUpdateFallbackValues( this.entityContainer.entity );
				// Preserve is #1 priority, hence last-one
				this.preservePropertiesFromChange( oldEntity );

				// Detect changes and enable / disable update button - just after all create/update/preserve/fallbacks !
				const enableUpdate = this.checkEmptyValueInOptionalList( optionalControlsNotEmpty );  // update needed when optional field was reset
				this.entityContainer.form.valid && enableUpdate
				? this.entityContainer.form.markAsDirty()
				: this.entityContainer.form.markAsPristine();
				break;
		}

		this.updateControlState.next( true );
	}

	private getMappedAndSanitizedFormValues(): ENTITY_TYPE {
		const uncleanedEntity: ENTITY_TYPE = this.entityContainer.formService.mapValuesToEntity(
			this.entityContainer.form.getRawValue(),
			this.entityContainer.entity
		);

		/*
		 * "Security net": Even though all form element components should return
		 * `null` when their input field is empty, the line below ensures that
		 * no empty string will slip through.
		 */
		return ObjectUtils.sanitizeEmptyStringsToNull( uncleanedEntity );
	}

	/**
	 * Iterates form recursively (including FormGroups & FormArrays), collects
	 * controls which are optional and have a value.
	 * Controls are collected into the "accumulator" array passed in as the 2nd
	 * parameter.
	 *
	 * @param formGroup FormGroup to begin with ("form root")
	 * @param optionalControls "accumulator" array to collect the optional controls into
	 */
	private getOptionalValuesOfForm( formGroup: UntypedFormGroup | UntypedFormArray, optionalControls: AbstractControl[] ): void {
		/*
		 * TODO -- Potential for refactoring
		 *  The method should not change a passed-in array reference, but should
		 *  create the "accumulator" array on its own, "collect" the FormControl
		 *  objects of the optional fields into it (using `Array.prototype.filter()`
		 *  rather than `Array.prototype.forEach()`) and eventually return it.
		 */
		Object.keys( formGroup.controls ).forEach( key => {
			const abstractControl = formGroup.get( key );
			if ( abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray ) {
				this.getOptionalValuesOfForm( abstractControl, optionalControls );
			} else {
				const isOptional = this.isOptional( abstractControl );
				const hasValue = this.hasValue( abstractControl );

				if ( isOptional && hasValue ) {
					optionalControls.push( abstractControl );
				}
			}
		} );
	}

	/**
	 * Determines whether form control is optional or not.
	 *
	 * @return true = optional field, false = required field
	 */
	private isOptional( control: AbstractControl ): boolean {
		return !control.hasValidator( Validators.required );
	}

	/**
	 * Does control have a value ?
	 *
	 * @return true = has value, false = has no value (null, undefined)
	 */
	private hasValue( control: AbstractControl ): boolean {
		return !ObjectUtils.isNullish( control.value ) && control.value;
	}

	/**
	 * Detects any control with no value from input list.
	 *
	 * @param optionalControls list of form controls to detect missing values
	 * @return
	 * * `true` if list has at least one control with empty value
	 * * `false` if all controls have a value
	 */
	private checkEmptyValueInOptionalList( optionalControls: AbstractControl[] ): boolean {
		return optionalControls.some( control =>
			!this.hasValue( control )
		);
	}

	protected hasFormInvalidFields( showErrorsOnInvalidFields = true ): boolean {
		const hasFormInvalidFields = FormUtils.hasFormInvalidFields(
			this.entityContainer.form,
			this.logger,
			showErrorsOnInvalidFields
		);

		if ( hasFormInvalidFields && showErrorsOnInvalidFields ) {
			this.hideErrors = false;
		}

		return hasFormInvalidFields;
	}

	protected formSubmit(
		context,
		beforeApiRequest?: BeforeApiRequest,
		apiCreateRequestSuccess?: ApiCreateRequestSuccess,
		showSuccessToaster = true,
		apiCreateRequestFailure?: ApiCreateRequestFailure,
		showFailureToaster = true
	): void {
		// exit if form is invalid
		if ( this.hasFormInvalidFields() ) { return; }

		if ( this.entityContainer.form.valid ) {
			this.updateRequestInProgressAndPageLoader( true );
			this.entityContainer.entity = this.getMappedAndSanitizedFormValues()

			if ( beforeApiRequest ) {
				beforeApiRequest( context );
			}

			if ( this.formSubmitAction === FormSubmitActionEnum.EMIT_FORM_VALUES ) {
				this.formValuesEmitter.emit( this.entityContainer.form.getRawValue() );
				this.uiService.hidePageLoaderOrCountDown();
				return;
			}


			if ( this.formAction === FORM_ACTION.CREATE ) {
				this.submitCreateForm( context, apiCreateRequestSuccess, showSuccessToaster, apiCreateRequestFailure, showFailureToaster );
			} else {
				this.submitUpdateForm();
			}
		}
	}

	protected formRequestFinished( formResult: IFormResult<ENTITY_TYPE> ): void {}

	generateFormSubmissionResult( requestState: RequestState, entity?: ENTITY_TYPE ): IFormResult<ENTITY_TYPE> {
		return {
			action: this.formAction,
			entity,
			requestState,
			hideForm: requestState === RequestState.SUCCESS || requestState === RequestState.FAILED || this.formAction === FORM_ACTION.ABORT
		};
	}

	ngOnInit(): void {
		this.hideErrors = true;
		this.formConfigData = this.entityMetadata.form.baseData;
	}

	cancelForm(): void {
		this.formAction = FORM_ACTION.ABORT;
		const formResult = this.generateFormSubmissionResult( RequestState.NOT_ASKED );
		this.formRequestFinished( formResult );
	}

	clearValue( name: string ) {
		this.entityContainer.form.get( name ).setValue( '' );
	}

	hasRequiredValidator( formGroup: FormGroup, formEntityId: string ): boolean {
		return FormUtils.hasRequiredValidator( formGroup, formEntityId );
	}
}
